
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ShieldCheck, Zap, Mail, ArrowRight } from "lucide-react";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import PublicFaqSection from "@/components/PublicFaqSection";

interface ProviderInfo {
  id: string;
  name: string;
  active: boolean;
  brandColor: string;
  logoUrl: string;
  description: string;
}

const LOGO_DEV_TOKEN = "pk_UU8mekRUR8SO0NETcyNA-Q";
const logoDevUrl = (domain: string) =>
  `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=80&format=png`;

const providers: ProviderInfo[] = [
  {
    id: "jotform",
    name: "Jotform",
    active: true,
    brandColor: "#FF6100",
    logoUrl: logoDevUrl("jotform.com"),
    description: "Fully supported. Sign up today.",
  },
  {
    id: "formassembly",
    name: "FormAssembly",
    active: false,
    brandColor: "#00A88F",
    logoUrl: logoDevUrl("formassembly.com"),
    description: "Coming soon",
  },
  {
    id: "formstack",
    name: "Formstack",
    active: false,
    brandColor: "#2D8CFF",
    logoUrl: logoDevUrl("formstack.com"),
    description: "Coming soon",
  },
  {
    id: "typeform",
    name: "Typeform",
    active: false,
    brandColor: "#262627",
    logoUrl: logoDevUrl("typeform.com"),
    description: "Coming soon",
  },
  {
    id: "other",
    name: "Other",
    active: false,
    brandColor: "#6b7280",
    logoUrl: "",
    description: "Tell us what you need",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [providerEmails, setProviderEmails] = useState<Record<string, string>>({});
  const [submittedProviders, setSubmittedProviders] = useState<Set<string>>(new Set());
  const [submittingProvider, setSubmittingProvider] = useState<string | null>(null);
  const [customProviderName, setCustomProviderName] = useState("");

  const features = [
    {
      icon: <CheckCircle className="w-10 h-10 text-primary" />,
      title: "Exclusive Peace of Mind",
      description: "The ONLY automated backup solution for forms metadata. Be confident knowing your critical forms are protected.",
    },
    {
      icon: <ShieldCheck className="w-10 h-10 text-primary" />,
      title: "Independent & User-controlled",
      description: "We are a 3rd-party that gives you complete control of your forms, independent of any forms software provider.",
    },
    {
      icon: <Zap className="w-10 h-10 text-primary" />,
      title: "Automated & Secure Backups",
      description: "Set it and forget it. We handle your form backups seamlessly and securely, ensuring your form metadata is always safe.",
    },
  ];

  const LOOPS_FORM_URL = "https://app.loops.so/api/newsletter-form/cmmwjwe9803ki0i2l8m4cythi";

  const handleProviderVote = async (providerId: string) => {
    const email = providerEmails[providerId]?.trim();
    if (!email || !email.includes("@")) {
      toast({ variant: "destructive", title: "Please enter a valid email address." });
      return;
    }

    // Client-side rate limit (1 signup per minute, matching Loops' script)
    const now = Date.now();
    const lastSubmit = Number(localStorage.getItem("loops-form-timestamp") || "0");
    if (lastSubmit && lastSubmit + 60000 > now) {
      toast({ variant: "destructive", title: "Too many signups. Please try again in a moment." });
      return;
    }
    localStorage.setItem("loops-form-timestamp", String(now));

    setSubmittingProvider(providerId);
    try {
      const voteValue = providerId === "other" && customProviderName.trim()
        ? `other: ${customProviderName.trim()}`
        : providerId;

      const body = new URLSearchParams({
        email,
        userGroup: "",
        mailingLists: "",
        providerVote: voteValue,
      });

      const res = await fetch(LOOPS_FORM_URL, {
        method: "POST",
        body: body.toString(),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || res.statusText);
      }

      setSubmittedProviders((prev) => new Set(prev).add(providerId));
      const providerName = providers.find((p) => p.id === providerId)?.name || providerId;
      toast({
        title: providerId === "jotform" ? "You're on the list!" : "Vote counted!",
        description: providerId === "jotform"
          ? "Check your inbox to confirm your subscription."
          : `Your vote for ${providerName} has been recorded. Check your inbox to confirm.`,
      });
    } catch (err) {
      localStorage.setItem("loops-form-timestamp", "");
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast({ variant: "destructive", title: "Signup failed", description: message });
    } finally {
      setSubmittingProvider(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary to-primary/80 dark:from-primary/90 dark:to-primary/70 text-white py-20 md:py-32">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Never Lose Your Forms Again.
            </h1>
            <p className="text-lg md:text-xl mb-10 max-w-3xl mx-auto">
              Automated, secure backups for your critical forms. The peace of mind you need.
            </p>
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-10 py-4 text-lg" onClick={() => navigate("/auth?mode=signup")}>
              Get Started
            </Button>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">
              Simple Steps to Secure Your Forms
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">1. Connect</div>
                <p className="text-muted-foreground">Securely link your form provider account.</p>
              </div>
              <div className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">2. Confirm</div>
                <p className="text-muted-foreground">Make sure your forms are backing up without issue.</p>
              </div>
              <div className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">3. Relax</div>
                <p className="text-muted-foreground">We handle the rest, backing up your forms automatically.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Supported Providers Section */}
        <section className="py-16 md:py-24 bg-primary dark:bg-primary/90 text-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Supported Form Providers
              </h2>
              <p className="text-lg max-w-2xl mx-auto text-blue-100">
                FAB currently supports Jotform with more providers on the way.
                Subscribe to our newsletter and your signup counts as a vote for the provider you want next.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
              {providers.map((provider) => {
                const isSubmitted = submittedProviders.has(provider.id);
                const isSubmitting = submittingProvider === provider.id;

                return (
                  <div
                    key={provider.id}
                    className={`rounded-xl p-6 flex flex-col items-center text-center transition-all ${
                      provider.active
                        ? "bg-white/15 backdrop-blur-sm border-2 border-white/30"
                        : "bg-white/5 backdrop-blur-sm border border-white/10"
                    }`}
                  >
                    {/* Logo + Name */}
                    <div className={`flex flex-col items-center mb-3 ${!provider.active ? "grayscale" : ""}`}>
                      {provider.logoUrl ? (
                        <img
                          src={provider.logoUrl}
                          alt={`${provider.name} logo`}
                          className="h-12 w-12 rounded-lg object-contain mb-2"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center mb-2">
                          <span className="text-xl font-bold">?</span>
                        </div>
                      )}
                      <span className="text-lg font-semibold">{provider.name}</span>
                    </div>

                    {/* Status badge */}
                    {provider.active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold bg-green-500/20 text-green-200 px-2.5 py-1 rounded-full mb-3">
                        <CheckCircle className="w-3 h-3" />
                        Available Now
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-medium bg-white/10 text-blue-200 px-2.5 py-1 rounded-full mb-3">
                        Coming Soon
                      </span>
                    )}

                    <p className="text-sm text-blue-100 mb-4 min-h-[2.5rem]">
                      {provider.description}
                    </p>

                    {/* Email signup / vote */}
                    {!provider.active && isSubmitted ? (
                      <div className="w-full text-center py-2 mt-auto">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-300" />
                        <p className="text-xs text-green-200">Vote recorded!</p>
                      </div>
                    ) : (
                      <div className="w-full space-y-2 mt-auto">
                        {provider.id === "other" && (
                          <Input
                            type="text"
                            placeholder="Provider name"
                            value={customProviderName}
                            onChange={(e) => setCustomProviderName(e.target.value)}
                            className="h-9 text-sm bg-white/10 border-white/20 text-white placeholder:text-blue-200/50 focus:border-white/40"
                          />
                        )}
                        <div className="relative">
                          <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                          <Input
                            type="email"
                            placeholder="you@email.com"
                            value={providerEmails[provider.id] || ""}
                            onChange={(e) =>
                              setProviderEmails((prev) => ({ ...prev, [provider.id]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (provider.active) {
                                  const email = providerEmails[provider.id]?.trim();
                                  if (email) navigate(`/auth?mode=signup&email=${encodeURIComponent(email)}`);
                                } else {
                                  handleProviderVote(provider.id);
                                }
                              }
                            }}
                            className="pl-8 h-9 text-sm bg-white/10 border-white/20 text-white placeholder:text-blue-200/50 focus:border-white/40"
                          />
                        </div>
                        <Button
                          size="sm"
                          className="w-full h-8 text-xs font-medium bg-white text-primary hover:bg-gray-100"
                          disabled={isSubmitting}
                          onClick={() => {
                            if (provider.active) {
                              const email = providerEmails[provider.id]?.trim();
                              navigate(`/auth?mode=signup${email ? `&email=${encodeURIComponent(email)}` : ""}`);
                            } else {
                              handleProviderVote(provider.id);
                            }
                          }}
                        >
                          {isSubmitting ? (
                            "..."
                          ) : provider.active ? (
                            <>Get Started <ArrowRight className="w-3 h-3 ml-1" /></>
                          ) : (
                            <>Vote & Subscribe <ArrowRight className="w-3 h-3 ml-1" /></>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-center text-xs text-blue-200/70 mt-8 max-w-lg mx-auto">
              Subscribing signs you up for the FAB newsletter (double opt-in) and counts as a vote
              for the form provider you want us to support next.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12 md:mb-16">
              Why Choose Us?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {features.map((feature) => (
                <div key={feature.title} className="bg-card p-8 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow border">
                  <div className="flex justify-center mb-6">{feature.icon}</div>
                  <h3 className="text-2xl font-semibold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-primary dark:bg-primary/90 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Protect Your form metadata?
            </h2>
            <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Join today and experience the only dedicated backup solution for forms metadata.
            </p>
            <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-10 py-4 text-lg border-white dark:border-gray-700" onClick={() => navigate("/pricing")}>
              View Pricing & Plans
            </Button>
          </div>
        </section>

        <PublicFaqSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
